import React, {useState} from 'react';
import {
    useGetStorefrontOrdersQuery,
    useGetStorefrontOrderStatusesQuery,
    useGetStorefrontsQuery,
    useGetStorefrontOrdersForStorefrontSearchQuery
} from '../../../store/endpoints/StorefrontSettingsEndpoints';
import {IStorefrontOrder} from '../../../models/Storefront';
import StorefrontOrderPanel from './StorefrontOrderPanel';
import GridComposer from "../../GridComposer/GridComposer";
import {GridSelectionMode, IColumnDetails} from "../../../models/GridConfiguration";
import {IconButton, Modal, PrimaryButton} from "@fluentui/react";
import RichTextField from "../../Common/RichTextField";

interface StorefrontOrdersTableProps {
    storefront?: string;
}

// The open order is reflected in the URL (?orderId=...) so an order can be deep-linked (e.g. from the
// extension folio logs) and a refresh re-opens the same details panel.
const ORDER_QUERY_PARAM = 'orderId';

const StorefrontOrdersTable: React.FC<StorefrontOrdersTableProps> = ({ storefront }) => {
    const [selectedOrder, setSelectedOrder] = React.useState<string | undefined>(
        () => new URLSearchParams(window.location.search).get(ORDER_QUERY_PARAM) ?? undefined
    );
    const [selectedOrderTab, setSelectedOrderTab] = useState<string>('details');
    const [page, setPage] = useState<number>(1)
    const [pageSize, setPageSize] = useState<number>(25)
    const [filter, setFilter] = useState<string>()
    const [filterField, setFilterField] = useState<string>()
    const [sortBy, setSortBy] = useState<string>()
    const [sortDirection, setSortDirection] = useState<string>()
    const [presentedTermsOrder, setPresentedTermsOrder] = useState<IStorefrontOrder | undefined>(undefined);
    const conditions = { page, pageSize, searchTerm: filter, searchBy: filterField, sortBy: sortBy ?? 'createDate', sortDirection: sortDirection ?? 'desc' };
    const globalOrders = useGetStorefrontOrdersQuery({ conditions }, { skip: !!storefront });
    const scopedOrders = useGetStorefrontOrdersForStorefrontSearchQuery({ storefrontId: storefront ?? '', conditions }, { skip: !storefront });
    const orders = storefront ? scopedOrders : globalOrders;
    const possibleStorefronts = useGetStorefrontsQuery();
    const possibleStorefrontStatuses = useGetStorefrontOrderStatusesQuery();

    // Keep the URL in sync with the open order so the panel is shareable/deep-linkable and survives a refresh.
    React.useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (selectedOrder) {
            params.set(ORDER_QUERY_PARAM, selectedOrder);
        } else {
            params.delete(ORDER_QUERY_PARAM);
        }
        const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
        window.history.replaceState({}, '', newUrl);
    }, [selectedOrder]);

    const getCustomColumns = () => {
        const columnDetails: IColumnDetails[] = [
            {
                index: 0,
                identifier: 'Details',
                label: '',
                type: '',
                component:() => <PrimaryButton label={'Details'}>Details</PrimaryButton>,
                onClick: (order: IStorefrontOrder) => {setSelectedOrder(() => order.id)},
                required: true,
            },
            ...(!storefront ? [{
                identifier: 'storefront.keyword',
                label: 'Storefront',
                type: '',
                sortable: true,
                filterable: true,
                component:(order: IStorefrontOrder) => <div>{possibleStorefronts.data?.items?.find((a) => a.id === order.storefront)?.name}</div>,
                customFilterOptions: () => {
                    return possibleStorefronts.data?.items?.map((a) => ({key: a.id ?? "", text: a.name ?? ""}));
                },
                tooltip: 'Sort on Storefront is not alphabetical'
            } as IColumnDetails] : []),
            {
                identifier: 'status.keyword',
                label: 'Status',
                type: '',
                sortable: true,
                filterable: true,
                customFilterOptions: () => {
                    if(possibleStorefrontStatuses?.data) {
                        return Object.entries(possibleStorefrontStatuses.data)?.map(([key, text]) => ({key, text}));
                    }
                }
            },
            {
                identifier: 'workspaceId.keyword',
                label: 'Workspace',
                type: '',
                sortable: false,
                filterable: false,
                component: (order: IStorefrontOrder) => {
                    return(
                        <React.Fragment>
                            {order.workspaceId ? 
                            <IconButton iconProps={{iconName: 'link'}} onClick={() => window.open(`${window.location.origin}/workspace/${order.workspaceId}`, '_blank')}/> 
                            : undefined}
                        </React.Fragment>
                    )
                }
            },
            {
                identifier: 'preparedCartId.keyword',
                label: 'Prepared Cart',
                type: '',
                sortable: false,
                filterable: false,
                component: (order: IStorefrontOrder) => {
                    return(
                        <React.Fragment>
                            {order.preparedCartId ?
                                <IconButton iconProps={{iconName: 'link'}} onClick={() => window.open(`${window.location.origin}/workspace/${order.preparedCartId}?showPreparedCart`, '_blank')}/>
                                : undefined}
                        </React.Fragment>
                    )
                }
            },
            {
                identifier: 'shipments.shipmentCarrier.keyword',
                label: 'Carrier',
                type: '',
                sortable: false,
                filterable: false,
                component: (order: IStorefrontOrder) => {
                    const display: Record<string, string> = {
                        FEDEX: 'FedEx', UPS: 'UPS', USPS: 'USPS',
                        DHL: 'DHL', AMAZON: 'Amazon Logistics', ONTRAC: 'OnTrac',
                    };
                    const carrier = order.shipments?.[0]?.shipmentCarrier;
                    return <span>{carrier ? (display[carrier] ?? carrier) : undefined}</span>;
                },
            },
            {
                identifier: 'shipments.shipmentTrackingNumber.keyword',
                label: 'Tracking Number',
                type: '',
                sortable: false,
                filterable: false,
                component: (order: IStorefrontOrder) => {
                    const shipment = order.shipments?.[0];
                    return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span>{shipment?.shipmentTrackingNumber}</span>
                            {shipment?.shipmentTrackingUrl && shipment?.shipmentTrackingNumber && (
                                <IconButton
                                    iconProps={{ iconName: 'Link' }}
                                    ariaLabel="Open tracking link"
                                    onClick={() => window.open(shipment.shipmentTrackingUrl, '_blank', 'noopener,noreferrer')}
                                />
                            )}
                        </div>
                    );
                },
            },
            {
                identifier: 'presentedTerms.keyword',
                label: 'Presented Terms',
                type: '',
                sortable: false,
                filterable: false,
                component: (order: IStorefrontOrder) => {
                    return (
                        <React.Fragment>
                            {order.presentedTerms ?
                                <IconButton iconProps={{iconName: 'CheckList'}} onClick={() => setPresentedTermsOrder(order)} />
                                : undefined}
                        </React.Fragment>
                    )
                }
            },
            {
                identifier: 'shipments',
                label: 'Shipments',
                type: '',
                sortable: false,
                filterable: false,
                component: (order: IStorefrontOrder) => {
                    if (!order.shipments) return <></>;
                    let shipmentNumber = 0;
                    return (
                        <div style={{ display: 'flex', flexWrap: 'wrap', maxWidth: '25vw', alignItems: 'center', gap: '4px' }}>
                            <IconButton iconProps={{iconName: "view"}} onClick={() => { setSelectedOrder(order.id); setSelectedOrderTab('shipping'); }}/>
                            {order.shipments.map((s, idx) => {
                                if (!s.shipmentTrackingUrl) return null;
                                shipmentNumber++;
                                return (
                                    <div key={idx} className={"ac-search-filter"} style={{ marginTop: 0 }}>
                                        Shipment {shipmentNumber}:
                                        <IconButton iconProps={{iconName: "link"}} onClick={() => window.open(s.shipmentTrackingUrl, '_blank', 'noopener,noreferrer')}/>
                                    </div>
                                );
                            })}
                        </div>
                    );
                }
            }
        ];
        return columnDetails
    }

    return (<div style={{overflowX: 'auto'}}>
        <GridComposer
            data={orders.data ?? {}}
            page={page}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            loading={orders.isLoading || orders.isFetching}
            onRefresh={() => orders.refetch()}
            selectionMode={GridSelectionMode.multiple}
            columnSetName={'StorefrontOrder'}
            filter={filter}
            filterField={filterField}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={(column, direction) => {
                setSortBy(() => column)
                setSortDirection(() => direction)
            }}
            onFilter={(filter, column) => {
                setFilter(() => filter)
                setFilterField(() => column)
            }}
            overrideColumns={getCustomColumns()}
            canDragColumns={true}
            canResizeColumns={true}
            canCustomizeColumns={true}
            canChangePageSize={true}
            canCreateCsv={true}
            getItemId={(item: IStorefrontOrder) => item.id}
        />
        <Modal
            isOpen={!!presentedTermsOrder}
            onDismiss={() => setPresentedTermsOrder(undefined)}
            isBlocking={false}
            styles={{ main: { padding: '1em', minWidth: 600, maxWidth: 900, width: '80vw' } }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1em' }}>
                <strong>Presented Terms</strong>
                <IconButton iconProps={{ iconName: 'Cancel' }} onClick={() => setPresentedTermsOrder(undefined)} />
            </div>
            <RichTextField
                onChange={() => console.log('read only field')}
                label="Terms"
                value={presentedTermsOrder?.presentedTerms}
                readOnly={true}
                mode="html"
            />
        </Modal>
        <StorefrontOrderPanel
            order={selectedOrder}
            onDismiss={() => setSelectedOrder(undefined)}
            initialTab={selectedOrderTab}
            />
    </div>
    );
}

export default StorefrontOrdersTable;